<template>
  <three-point-menu ref="menu" density="comfortable">
    <!-- Choose address -->
    <address-select-dialog
      v-if="addressSelection.availableAddresses.length > 0"
      ref="select-address-dialog"
      v-model="selectedAddressValue"
      :addresses="addressSelection.availableAddresses"
      :show-select-hint="showSelectHint"
      @cancel="closeMenu"
      @select="selectAddress"
    >
      <template #activator="{ props }">
        <v-list-item ref="select-address-button" v-bind="props" :disabled="readonly">
          <v-list-item-title>{{ $t('chooseAddress') }}</v-list-item-title>
        </v-list-item>
      </template>
    </address-select-dialog>

    <!-- Add new address -->
    <address-form-dialog
      v-if="addressSelection.canAdd"
      ref="add-address-dialog"
      :address-data="createEmptyAddress()"
      :show-delete-button="false"
      :disable-default-billing-address-checkbox="false"
      :disable-default-delivery-address-checkbox="false"
      :show-default-address-checkboxes="!isQuotesPortal"
      :title="$t('addAddress').toString()"
      show-postal-address-only
      @save="createAddress"
      @cancel="closeMenu"
    >
      <template #activator="{ props }">
        <v-list-item ref="add-address-button" v-bind="props" :disabled="readonly">
          <v-list-item-title>{{ $t('addAddress') }}</v-list-item-title>
        </v-list-item>
      </template>
    </address-form-dialog>

    <!-- Edit address -->
    <address-form-dialog
      v-if="selectedAddress"
      ref="edit-address-dialog"
      :address-data="selectedAddress"
      :show-delete-button="false"
      :show-default-address-checkboxes="false"
      show-postal-address-only
      :title="$t('editAddress')"
      @save="saveAddress"
      @cancel="closeMenu"
    >
      <template #activator="{ props }">
        <v-list-item ref="edit-address-button" v-bind="props" :disabled="readonly">
          <v-list-item-title>{{ $t('editAddress') }}</v-list-item-title>
        </v-list-item>
      </template>
    </address-form-dialog>

    <!-- Edit Contact data -->
    <contact-data-form-dialog
      v-if="contactData"
      ref="edit-contact-data-dialog"
      :contact="contactData"
      :show-delete-button="false"
      disable-email-input
      @save="saveContactData"
      @cancel="closeMenu"
    >
      <template #activator="{ props }">
        <v-list-item ref="edit-contact-data-button" v-bind="props" :disabled="readonly">
          <v-list-item-title>{{ $t('editContactData') }}</v-list-item-title>
        </v-list-item>
      </template>
    </contact-data-form-dialog>
  </three-point-menu>
</template>

<script lang="ts" setup>
import ThreePointMenu from '@/components/three-point-menu/ThreePointMenu.vue'
import { isQuotesPortal } from '@/helpers'
import AddressFormDialog from '@/modules/address/components/AddressFormDialog.vue'
import AddressSelectDialog from '@/modules/address/components/AddressSelectDialog.vue'
import { createEmptyAddress } from '@/modules/address/helpers'
import ContactDataFormDialog from '@/modules/contactData/components/ContactDataFormDialog.vue'
import { GuestQuoteEvent } from '@/modules/tracking/events/quote'
import { trackEvent } from '@/modules/tracking/useTracking'
import { AddressSelection, ContactData, ShopAddress } from '@/types/index'
import { clone } from 'lodash-es'
import { PropType, ref, useTemplateRef } from 'vue'

const componentProps = defineProps({
  selectedAddress: {
    type: Object as PropType<ShopAddress | undefined>,
    required: true
  },
  addressSelection: {
    type: Object as PropType<AddressSelection>,
    required: true
  },
  contactData: {
    type: Object as PropType<ContactData>,
    default: undefined
  },
  showSelectHint: {
    type: Boolean,
    required: true
  },
  readonly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['create', 'save', 'select', 'saveContactData'])

const menu = useTemplateRef('menu')
const selectedAddressValue = ref(clone(componentProps.selectedAddress))

const createAddress = (address: ShopAddress) => {
  trackEvent(GuestQuoteEvent.ChangeAddress.Added)
  emit('create', address)
  closeMenu()
}

const selectAddress = () => {
  trackEvent(GuestQuoteEvent.ChangeAddress.Chosen)
  emit('select', selectedAddressValue.value)
  closeMenu()
}

const saveAddress = (address: ShopAddress) => {
  trackEvent(GuestQuoteEvent.ChangeAddress.Edited)
  emit('save', address)
  closeMenu()
}

const saveContactData = (updatedContactData: ContactData) => {
  trackEvent(GuestQuoteEvent.ChangeContactData)
  emit('saveContactData', updatedContactData)
  closeMenu()
}

const closeMenu = () => {
  if (menu.value) {
    menu.value.close()
  }
}
</script>
